import {
	CalibrationIcon,
	ChartCombinedIcon,
	DashboardIcon,
	HrFlagIcon,
	LinearScaleIcon,
	LogoutIcon,
	LoopIcon,
	ReportIcon,
	Sidebar as SidebarBase,
	Snap360Icon,
	UsersIcon,
} from '@viapeople/components'
import { useEffect } from 'react'
import logoGray from '/logo-grayscale.png'
import spiralinksLogo from '/spiralinks-logo.jpeg'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import type { SidebarStoreInstance } from './store'
import type { InitialData } from './types'

type Action = InitialData['menuItems'][number]
export interface SidebarProps {
	store: SidebarStoreInstance
	logoSrc?: string
	defaultCollapsed?: boolean
	selectedMenuItem?: string
	onCollapsedChange?: (collapsed: boolean) => void
}
type ActionId =
	| 'dashboard'
	| 'reports'
	| 'calibrations'
	| 'users'
	| 'loops'
	| 'pec'
	| 'enps'
	| 'spiralinks'
	| 'dealteam'
	| 'hr-review-flags'
	| 'logout'

function resolveIcon(action: Action) {
	const iconMap = {
		dashboard: DashboardIcon,
		reports: ReportIcon,
		calibrations: CalibrationIcon,
		users: UsersIcon,
		loops: LoopIcon,
		pec: Snap360Icon,
		enps: LinearScaleIcon,
		spiralinks: () => <img src={spiralinksLogo} alt='Focalreview logo' className='w-5 h-5' />,
		dealteam: ChartCombinedIcon,
		'hr-review-flags': HrFlagIcon,
		logout: LogoutIcon,
		default: null,
	}

	const icon = iconMap[action.id as unknown as ActionId]
	return {
		...action,
		icon,
	}
}

export const Sidebar: React.FC<SidebarProps> = ({ store, defaultCollapsed = true, onCollapsedChange }) => {
	const { state, error, initialize, clearError } = store()
	const { showToast } = useToast()
	const { isLoggedIn } = sessionStore()

	useEffect(() => {
		// Only initialize if user is logged in to prevent 401 errors during logout
		if (isLoggedIn) {
			initialize()
		}
	}, [initialize, isLoggedIn])

	useEffect(() => {
		// Only show errors if user is logged in to prevent error flashes during logout
		if (error && isLoggedIn) {
			showToast(error, 'error')
			clearError()
		} else if (error) {
			// Silently clear error if not logged in (likely during logout)
			clearError()
		}
	}, [error, clearError, showToast, isLoggedIn])

	return (
		<SidebarBase
			logoSrc={logoGray}
			menuItems={state.menuItems.map(resolveIcon)}
			bottomActions={state.bottomActions.map(resolveIcon)}
			defaultCollapsed={defaultCollapsed}
			onCollapsedChange={onCollapsedChange}
		/>
	)
}
